import React from 'react';
import LegalDocument from '../../components/LegalDocument';

const meta = [
  { label: 'Effective', value: '1 October 2026' },
  { label: 'Version', value: '1.0' },
  { label: 'Data controller', value: 'Red Pill Digital Solutions (Pvt) Ltd' },
];

const sections = [
  {
    id: 'introduction',
    title: '1. Introduction',
    body: [
      'KidQuest is a parent- and legal-guardian-operated discovery and booking platform for children’s activities, classes, events and experiences in Sri Lanka. It is operated by Red Pill Digital Solutions (Pvt) Ltd (“KidQuest”, “we”, “us”). This Privacy Policy explains how we collect, use, share, retain and protect personal information through the iOS app, Android app, website, host portal and support services. We seek to comply with Sri Lanka’s Personal Data Protection Act No. 9 of 2022, as amended, and other applicable laws. Where the GDPR applies, we will meet its relevant requirements.',
    ],
  },
  {
    id: 'who-uses-kidquest',
    title: '2. Who Uses KidQuest',
    body: [
      'KidQuest is a parent- and legal-guardian-operated platform. Only adults acting as parents or legal guardians may create and manage accounts, bookings, payments and child profiles. Children cannot create independent KidQuest accounts or directly make bookings or payments.',
    ],
  },
  {
    id: 'information-we-collect',
    title: '3. Personal Information We Collect',
    body: [
      'Parent/guardian data may include name, email, telephone number, authentication information, selected location, activity interests, booking history, transaction references and support enquiries. Child profiles are created and controlled by the parent or guardian and may contain a nickname or display name, age band and parent-selected interests. A child’s real or legal name is not required to create or maintain a child profile. Additional participant details, including a legal name or relevant safety information, are requested separately only when necessary for a particular booking, with an explanation of purpose and appropriate authorisation. Technical data may include app version, device information, IP address, security logs and diagnostics, as applicable to the actual app.',
    ],
  },
  {
    id: 'how-we-obtain-information',
    title: '4. How We Obtain Information',
    body: [
      'We receive information directly from adult account holders and Hosts; through bookings, payments and support requests; from app and website technical events; and from approved service providers. We explain collection purposes at relevant points of entry. We do not intentionally seek personal information directly from children.',
    ],
  },
  {
    id: 'why-we-use-information',
    title: '5. Why We Use Information',
    body: [
      'We use information to authenticate accounts; discover and recommend suitable activities; facilitate registration and bookings; confirm payments and issue receipts; send operational reminders and schedule changes; enable Hosts to deliver booked services; respond to support and refund requests; review Host eligibility and safety concerns; protect the service from misuse; meet legal and accounting obligations; and improve app performance. Promotional communications are sent only where permitted and subject to any required consent. We do not use children’s information for behavioural advertising.',
    ],
  },
  {
    id: 'legal-grounds',
    title: '6. Legal Grounds for Processing',
    body: [
      'Depending on the activity and applicable law, processing may be necessary to perform a contract, comply with a legal obligation, pursue a permitted legitimate interest with appropriate safeguards, protect vital interests, or be based on consent. Optional location access, marketing and processing of certain sensitive information may require separate consent. Consent can be withdrawn where it is the applicable basis, without affecting earlier lawful processing.',
    ],
  },
  {
    id: 'childrens-privacy',
    title: '7. Children’s Privacy and Guardian Control',
    body: [
      'KidQuest is intended for adults acting as parents or legal guardians; children cannot independently create accounts. A parent or guardian may create a child profile using a nickname or preferred display name. A child’s real or legal name is not required for profile creation or maintenance. Guardians control profile information and may edit or request deletion of child profiles. Where an organiser genuinely requires a participant’s legal name or essential participation or safety details, those details are requested separately during booking, with a clear explanation and appropriate authorisation. KidQuest minimises collection and restricts access to children’s information. We do not use children’s information for targeted advertising, behavioural marketing, third-party advertising profiles or unrelated commercial profiling.',
    ],
  },
  {
    id: 'information-shared-with-hosts',
    title: '8. Information Shared with Hosts',
    body: [
      'For a confirmed booking, KidQuest may disclose to the relevant Host only information reasonably necessary to administer the activity, such as guardian contact details, booking reference, participant count, relevant age band and separately provided participant or safety details when required. Hosts must use such data solely for booking administration, activity delivery and safeguarding, and must not use it for unrelated marketing or profiling. Host obligations and independent data-controller responsibilities, where applicable, are addressed through Host agreements and appropriate privacy notices.',
    ],
  },
  {
    id: 'payments-and-refunds',
    title: '9. Payments and Refund Information',
    body: [
      'Payments may be facilitated by PayHere, Commercial Bank or other disclosed authorised payment providers. These providers may independently process cardholder details, transaction identifiers and fraud checks under their own notices. KidQuest intends to use hosted/tokenised payment methods and not store full card numbers or security codes. We retain payment status and references for reconciliation, refunds, disputes and legal compliance. The separate KidQuest Terms & Conditions and Refund Policy govern cancellation and refund eligibility.',
    ],
  },
  {
    id: 'service-providers',
    title: '10. Service Providers and Disclosures',
    body: [
      'KidQuest may use AWS infrastructure, payment service providers including PayHere or Commercial Bank, and other technical providers actually integrated into the service (such as authentication, notifications, crash diagnostics or analytics). We disclose personal information only where necessary for the specified purpose and subject to applicable safeguards. We do not sell children’s personal information.',
    ],
  },
  {
    id: 'storage-and-transfers',
    title: '11. Storage and International Transfers',
    body: [
      'KidQuest uses Amazon Web Services infrastructure. Where personal data is processed or accessed outside Sri Lanka, KidQuest will apply the safeguards and conditions required under Sri Lanka’s Personal Data Protection Act, No. 9 of 2022, as amended, and any applicable regulations, rules or directions.',
    ],
  },
  {
    id: 'security-measures',
    title: '12. Security Measures',
    body: [
      'We use risk-appropriate safeguards, which may include encryption in transit and at rest, role-based permissions, secure authentication, restricted child-data access, audit logs, backup and recovery controls, vulnerability management and incident-response procedures. No online system is completely risk-free. Users should protect their credentials and report suspected misuse promptly.',
    ],
  },
  {
    id: 'retention-and-deletion',
    title: '13. Retention and Deletion',
    body: [
      'KidQuest retains personal data only for as long as necessary for the lawful and disclosed purposes for which it was collected, and in accordance with Sri Lanka’s Personal Data Protection Act, No. 9 of 2022, as amended (PDPA), applicable regulations and directions, and other relevant legal obligations. The PDPA does not establish one universal retention period for all categories of data. KidQuest therefore maintains category-specific retention and deletion rules based on purpose, necessity, safeguarding, accounting, taxation, dispute resolution and other applicable obligations. Child-profile and participation information will be deleted or anonymised when no longer necessary, unless continued retention is legally justified. Users may request deletion, subject to lawful exceptions. Data that must be retained will be protected and access-restricted, and securely deleted or anonymised when the applicable retention requirement ends.',
    ],
  },
  {
    id: 'privacy-rights',
    title: '14. Privacy Rights',
    body: [
      'Subject to applicable law, individuals may request access, correction, erasure, withdrawal of consent and other applicable rights concerning processing, including objections or complaints. Parents and legal guardians may exercise relevant rights for children they lawfully represent. Contact hello@kidquest.lk. We may verify identity before acting and will respond within applicable statutory timelines. Complaints may also be submitted to the Data Protection Authority of Sri Lanka where its jurisdiction and procedures apply.',
    ],
  },
  {
    id: 'account-deletion',
    title: '15. Account Deletion',
    body: [
      'Adult account holders may initiate account deletion from within the iOS app through Profile > Settings > Account Management > Delete Account. Users may also contact hello@kidquest.lk for support. KidQuest will delete or anonymise associated personal information, including child profiles, unless retention is required or permitted under the PDPA or other applicable law. Necessary booking, payment, accounting, dispute or safeguarding records may be retained for the relevant lawful period, with restricted access. Deleting an account does not automatically cancel an existing booking; applicable booking and refund terms continue to apply.',
    ],
  },
  {
    id: 'notifications-and-marketing',
    title: '16. Notifications and Marketing',
    body: [
      'Booking confirmations, reminders, changes, receipts and safety alerts are service messages. Optional promotional messages are sent where permitted, with appropriate opt-out or consent controls. We do not market directly to children or use children’s information for targeted advertising.',
    ],
  },
  {
    id: 'recommendations-and-analytics',
    title: '17. Recommendations and Analytics',
    body: [
      'Child-profile information, including nickname, age band, selected location and parent-selected interests, is used solely to personalise event and activity recommendations within KidQuest. A real or legal name is not required for recommendations or child-profile creation. Child-profile information is not used for targeted advertising, behavioural marketing, third-party advertising profiles or unrelated commercial profiling. KidQuest will assess any analytics, recommendation tools or third-party SDKs to ensure their actual data uses remain consistent with this commitment.',
    ],
  },
  {
    id: 'cookies-and-permissions',
    title: '18. Cookies and Device Permissions',
    body: [
      'The website may use essential cookies for login, security and preferences. Non-essential analytics or advertising cookies require appropriate notice and consent where applicable. Device location and push notifications use operating-system permissions; users can change these in device settings. Any tracking that triggers Apple’s App Tracking Transparency requirements will require the corresponding permission and accurate App Store disclosures.',
    ],
  },
  {
    id: 'photos-reviews-content',
    title: '19. Photos, Reviews and User Content',
    body: [
      'Hosts must have necessary rights and permissions before uploading images or videos, especially those identifying children. Reviews should avoid revealing unnecessary child information. KidQuest may moderate or remove content that breaches privacy, intellectual property or safeguarding rules. Concerns can be reported to hello@kidquest.lk.',
    ],
  },
  {
    id: 'security-incidents',
    title: '20. Security Incidents',
    body: [
      'We will investigate suspected data breaches, contain risks and notify regulators or affected individuals where required by applicable law. Incidents involving children’s information receive heightened attention. Contact hello@kidquest.lk or 070 555 9558 to report a concern.',
    ],
  },
  {
    id: 'external-services',
    title: '21. External Services',
    body: [
      'Activity Hosts, payment providers and linked external websites may have their own privacy practices. We encourage guardians to review relevant notices before supplying information directly. KidQuest remains responsible for its own processing obligations under applicable law.',
    ],
  },
  {
    id: 'geographic-scope',
    title: '22. Geographic Scope',
    body: [
      'KidQuest facilitates activities in Sri Lanka and is intended primarily for Sri Lankan users. Additional privacy laws may apply to certain users or processing activities depending on the circumstances.',
    ],
  },
  {
    id: 'changes-to-this-policy',
    title: '23. Changes to This Policy',
    body: [
      'We may update this notice for legal, technical or product changes. The latest version will appear in the app and on the website. Where material changes require notification or fresh consent, we will provide it before the relevant processing begins.',
    ],
  },
  {
    id: 'complaints-and-contact',
    title: '24. Complaints and Contact',
    body: [
      'For privacy requests, access, correction, deletion, safeguarding or security enquiries, contact KidQuest – Privacy & Customer Support, Red Pill Digital Solutions (Pvt) Ltd, No. 09, Balapokuna Road, Kirulapone, Colombo 05, Sri Lanka. Email: hello@kidquest.lk. Phone: 070 555 9558. Effective date: 1 October 2026.',
    ],
  },
];

const PrivacyPolicy = () => (
  <LegalDocument
    path="/privacy-policy"
    eyebrow="KidQuest / Privacy & Trust"
    title="Privacy Policy"
    subtitle="Parent & guardian operated platform"
    meta={meta}
    sections={sections}
  />
);

export default PrivacyPolicy;
